import { ArrowRight } from "lucide-react";
import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

const POLICY_PATHS = [
  "/term-of-use",
  "/privacy-policy",
  "/cancellation-policy",
  "/shipping-delivery-policy",
];

const navLinkClass = ({ isActive }) =>
  "block py-2 pl-3 pr-4 no-underline border-b border-[#0d2f27]/[0.08] transition-colors duration-200 hover:bg-[#eef6f0] lg:hover:bg-transparent lg:border-0 lg:hover:text-[#1f8a4f] lg:p-0 " +
  (isActive ? "text-[#1f8a4f] focus:text-[#1f8a4f]" : "text-[#0d2f27] focus:text-[#0d2f27]");

const Header = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [expended, setExpended] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const dropdownRef = useRef(null);

  return (
    <>
      <nav className="py-2.5 bg-[#f6f5f0] border-b border-[#0d2f27]/[0.08] sticky top-0 z-[9] max-[1024px]:px-3">
        <div className="flex flex-wrap items-center justify-between max-w-screen-xl mx-auto">
          <a href="/" className="flex items-center">
            <img
              src="/assets/img/logo/swastha-opd-logo-1.png"
              width={120}
              className="max-[768px]:w-[118px]"
              alt="Swasth OPD Logo"
            />
          </a>
          <div className="flex items-center lg:order-2">
            <div className="hidden mt-2 mr-4 sm:inline-block">
              <span />
            </div>
            <a
              href="/login.html"
              target="_blank"
              className="group inline-flex items-center justify-center px-[22px] py-[10px] rounded-md text-[15px] font-bold leading-[1.2] no-underline transition duration-200 bg-[#d6ea4f] text-[#0d2f27] hover:bg-[#e1f165] hover:-translate-y-px"
            >
              Login
              <ArrowRight
                size={16}
                className="ml-1 transition-transform duration-300 ease-in-out group-hover:translate-x-1"
              />
            </a>
            <button
              data-collapse-toggle="mobile-menu-2"
              type="button"
              className="inline-flex items-center p-2 ml-1 text-sm text-[#0d2f27] rounded-lg lg:hidden hover:bg-[#eef6f0] focus:outline-none focus:ring-2 focus:ring-[#d6ea4f]"
              aria-controls="mobile-menu-2"
              aria-expanded="false" onClick={() => setExpended(!expended)}
            >
              <span className="sr-only">Open main menu</span>
              <svg
                className="w-6 h-6"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <svg
                className="hidden w-6 h-6"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
          <div
            className={"items-center justify-between w-full lg:flex lg:w-auto lg:order-1 " + (expended ? "" : "hidden")}
            id="mobile-menu-2"
          >
            <ul className="flex flex-col m-0 font-semibold lg:flex-row lg:space-x-8 lg:mt-0 max-[1024px]:pl-0">
              <li>
                <NavLink to="/" end className={navLinkClass}>
                  Home
                </NavLink>
              </li>
              <li>
                <NavLink to="/plans" className={navLinkClass}>
                  Plans
                </NavLink>
              </li>
              <li className="customPaddingStyle" ref={dropdownRef}>
                <button
                  className={`pb-2 dropdownButton pl-3 pr-4 border-b border-[#0d2f27]/[0.08] transition-colors duration-200 hover:bg-[#eef6f0] lg:hover:bg-transparent lg:border-0 lg:hover:text-[#1f8a4f] lg:p-0 flex items-center cursor-pointer ${POLICY_PATHS.includes(pathname) ? "text-[#1f8a4f]" : "text-[#0d2f27]"}`}
                  type="button"
                  onClick={() => setOpen(!open)}
                >
                  Terms & Policies
                  <svg
                    className="w-2.5 h-2.5 ms-3"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 10 6"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="m1 1 4 4 4-4"
                    />
                  </svg>
                </button>
                {open && (
                  <div className="z-10 mt-2 bg-white absolute rounded-2xl ring-1 ring-black/5 shadow-[0_10px_40px_-20px_rgba(13,47,39,0.25)] w-fit overflow-hidden">
                    <ul className="py-2 text-sm text-[#5b6b66] pl-0">
                      <li>
                        <Link
                          to="/term-of-use"
                          className="block px-4 text-[#5b6b66] py-2 no-underline transition-colors duration-200 hover:bg-[#eef6f0] hover:text-[#0d2f27] focus:text-[#0d2f27]"
                        >
                          Terms Of Use
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="privacy-policy"
                          className="block px-4 text-[#5b6b66] py-2 no-underline transition-colors duration-200 hover:bg-[#eef6f0] hover:text-[#0d2f27] focus:text-[#0d2f27]"
                        >
                          Privacy Policy
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="cancellation-policy"
                          className="block px-4 text-[#5b6b66] py-2 no-underline transition-colors duration-200 hover:bg-[#eef6f0] hover:text-[#0d2f27] focus:text-[#0d2f27]"
                        >
                          Cancellation Policy
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/shipping-delivery-policy"
                          className="block px-4 text-[#5b6b66] py-2 no-underline transition-colors duration-200 hover:bg-[#eef6f0] hover:text-[#0d2f27] focus:text-[#0d2f27]"
                        >
                          Shipping And Delivery Policy
                        </Link>
                      </li>
                    </ul>
                  </div>
                )}
              </li>
              <li>
                <NavLink to="/contact" className={navLinkClass}>
                  Contact
                </NavLink>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Header;
